import useBackendClient from "@/client";
import { PricingTemplate, PricingTemplateDetails } from "@/modules/IAP/types";
import { ref } from "vue";
import useToast from "@/hooks/useToastNotification";

const systemPricingTemplates = ref<PricingTemplate[]>([]);
const appPricingTemplates = ref<PricingTemplate[]>([]);
const pricingTemplate = ref<PricingTemplateDetails | null>(null);

export type CreateAppPricingTemplateRequest = {
  pricingTemplateName: string;
  defaultCurrencyCode: "EUR" | "USD";
  templateDefaultPrice: string;
};

export type PricePointUpdate = {
  country: string;
  finalPriceAmountMicros: number;
};

export default function usePricingTemplates() {
  const { client } = useBackendClient();
  const { toastError } = useToast();

  async function getSystemPriceTemplates(appId: string) {
    try {
      const baseUrl = `/app/${appId}/pricingTemplate/systemTemplates`;
      const { data } = await client.get<{ data: PricingTemplate[] }>(`${baseUrl}`);
      systemPricingTemplates.value = data.data;
    } catch (error) {
      toastError({
        detail: "An error occurred while fetching pricing templates.",
        summary: "Error",
      });
    }
  }

  async function getAppPriceTemplates(appId: string) {
    try {
      const baseUrl = `/app/${appId}/pricingTemplate`;
      const { data } = await client.get<{ data: PricingTemplate[] }>(`${baseUrl}`);
      appPricingTemplates.value = data.data;
    } catch (error) {
      toastError({
        detail: "An error occurred while fetching pricing templates.",
        summary: "Error",
      });
    }
  }

  async function getPriceTemplateDetails(appId: string, templateId: string) {
    try {
      pricingTemplate.value = null;
      const baseUrl = `/app/${appId}/pricingTemplate`;
      const response = await client.get(`${baseUrl}/${templateId}`);
      pricingTemplate.value = response.data;
    } catch (error) {
      toastError({
        detail: `An error occurred while fetching pricing template ${templateId}.`,
        summary: "Error",
      });
    }
  }

  async function linkPricingTemplateToIAP(appId: string, templateId: string, iapId: string) {
    try {
      return await client.put(`/app/${appId}/pricingTemplate/${templateId}/iapLink/${iapId}`);
    } catch (error) {
      toastError({
        detail: `An error occurred while linking pricing template ${templateId} to iap ${iapId}.`,
        summary: "Error",
      });

      return null;
    }
  }

  async function syncPricingTemplateIAPs(appId: string, templateId: string) {
    try {
      await client.put(`/app/${appId}/pricingTemplate/${templateId}/sync`);
      return true;
    } catch (error) {
      toastError({
        detail: `An error occurred while syncing pricing template ${templateId} iaps.`,
        summary: "Error",
      });

      return false;
    }
  }

  async function createPricingTemplate(appId: string, data: CreateAppPricingTemplateRequest) {
    try {
      const response = await client.post(`/app/${appId}/pricingTemplate/`, {
        pricingTemplateName: data.pricingTemplateName,
        defaultCurrencyCode: data.defaultCurrencyCode,
        defaultPriceInMicros: Math.round(Number(data.templateDefaultPrice) * 1000000),
      });
      return response.data;
    } catch (error) {
      toastError({
        detail: `An error occurred while creating the new pricing template.`,
        summary: "Error",
      });

      return null;
    }
  }

  async function updatePricingTemplatePricePoints(appId: string, templateId: string, pricePoints: PricePointUpdate[]) {
    try {
      const response = await client.put<PricingTemplateDetails>(
        `/app/${appId}/pricingTemplate/${templateId}/pricePoints`,
        { pricePoints }
      );
      pricingTemplate.value = response.data;
      return true;
    } catch (error) {
      toastError({
        detail: `An error occurred while updating the prices of pricing template ${templateId}. No price was changed.`,
        summary: "Error",
      });

      return false;
    }
  }

  async function unlinkPricingTemplateFromIAP(appId: string, pricingTemplateId: string, iapId: string) {
    try {
      const response = await client.delete(`/app/${appId}/pricingTemplate/${pricingTemplateId}/iapLink/${iapId}`);
      return response.status === 200;
    } catch (error) {
      toastError({
        detail: `An error occurred while unlinking pricing template and IAP.`,
        summary: "Error",
      });

      return null;
    }
  }

  return {
    systemPricingTemplates,
    appPricingTemplates,
    pricingTemplate,
    getAppPriceTemplates,
    getSystemPriceTemplates,
    getPriceTemplateDetails,
    linkPricingTemplateToIAP,
    syncPricingTemplateIAPs,
    createPricingTemplate,
    updatePricingTemplatePricePoints,
    unlinkPricingTemplateFromIAP,
  };
}
